import apiClient from '@/api';
import { listSites, queryLandowners, SITE_ROOT_API_URL } from '@/routes/landowner/constants';
import { QueryClient } from '@tanstack/react-query';
import { listDevices } from '../constants';
import { AntennaType, DeployDeviceMetadata, DeployDevicerequest, PvPanelType } from './types';
import { CompactProcessObject } from '@/routes/process-objects/list/types';
import { queryDeploymentTypes } from '@/routes/measurement-group/constants';
import { DeviceResponse } from '../types';
import { DeployDeviceFormSchemaType } from './deploy-device-schema';
import { LandownerResponse } from '@/routes/landowner/types';

export const ANTENNAS_ROOT_API_URL = '/api/v1/antennas';
export const PV_PANELS_ROOT_API_URL = '/api/v1/pv-panels';
export const DEVICE_DEPLOYMENT_ROOT_API_URL = '/api/v1/devices-deployment';

export function queryAntennas() {
	return {
		queryKey: ['antennas'],
		queryFn: () => apiClient.get<AntennaType[]>(ANTENNAS_ROOT_API_URL)
	};
}

export function queryPvPanels() {
	return {
		queryKey: ['pv-panels'],
		queryFn: () => apiClient.get<PvPanelType[]>(PV_PANELS_ROOT_API_URL)
	};
}

export function queryProcessObjectsBySiteId(siteId: string) {
	return apiClient.get<CompactProcessObject[]>(`${SITE_ROOT_API_URL}/${siteId}/process-objects/summary`);
}

export function deployDevice(payload: DeployDevicerequest) {
	const requestBody = new FormData();
	requestBody.append('file', payload.file);
	requestBody.append('metadata', payload.metadata);
	return apiClient.post<DeviceResponse>(DEVICE_DEPLOYMENT_ROOT_API_URL, requestBody);
}

export function deployDeviceLoader(queryClient: QueryClient) {
	return async () => {
		const sites = await queryClient.ensureQueryData(listSites({ showAvailableSpaces: true }));
		const devices = await queryClient.ensureQueryData(listDevices());

		const antennas = queryClient.ensureQueryData(queryAntennas());
		const pvPanels = queryClient.ensureQueryData(queryPvPanels());
		const deploymentTypes = queryClient.ensureQueryData(queryDeploymentTypes());
		const landowners = await queryClient.ensureQueryData(queryLandowners());

		return {
			sites: sites.data,
			devices: devices.data,
			antennas,
			pvPanels,
			deploymentTypes,
			landowners
		};
	};
}

export function toDeployDeviceMetadata(
	data: DeployDeviceFormSchemaType,
	landowners: LandownerResponse[]
): DeployDeviceMetadata {
	const parseNumber = (s: string) => parseInt(s, 10);

	const siteValue = data.deploymentSiteId[0].value;
	const deviceValue = data.deviceId[0].value;
	const antennaValue = data.antennaTypeId[0].value;
	const panelValue = data.pvPanelTypeId[0].value;
	const poIdValue = data.upload.processObjectId;

	const selectedLandowner = data.landownerExists
		? landowners.find(landowner => landowner.id === parseNumber(data.existingLandownerId?.[0]?.value ?? ''))
		: undefined;

	return {
		device: {
			deploymentSiteId: parseNumber(siteValue),
			deviceId: parseNumber(deviceValue),
			antennaTypeId: parseNumber(antennaValue),
			pvPanelTypeId: parseNumber(panelValue)
		},

		landowner: selectedLandowner
			? { ...selectedLandowner }
			: {
					...data.landowner,
					mailingAddress: data.landowner.mailingAddress,
					mobile: data.landowner.mobile,
					siteId: parseNumber(siteValue),
					initialDeploymentDate: data.landowner.initialDeploymentDate?.toISOString() ?? ''
				},

		fileTitle: data.upload.title,
		fileDescription: data.upload.description,

		fieldReport: {
			...data.fieldReport,
			poId: parseNumber(poIdValue),
			performedAt: data.fieldReport.performedAt?.toISOString(),
			notes: data.fieldReport.notes,
			hasExternalAntennas: data.fieldReport.hasExternalAntennas
		}
	};
}

export function undeployDevice(deviceId: number) {
	return apiClient.delete(`${DEVICE_DEPLOYMENT_ROOT_API_URL}/${deviceId}`);
}
